import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { destroyCookie, parseCookies, setCookie } from 'nookies'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { API_ERROR_MESSAGES } from '@/constants/apiErrorMessages'
import { ANON_TOKEN_COOKIE, TOKEN_COOKIE } from '@/constants/session'
import { describeIssues, reportContractIssue } from '@/helpers/contractLog'
import { clearAllStores } from '@/stores/clearAllStores'
import { useLoaderStore } from '@/stores/useLoaderStore'
import { ApiError, CONTRACT_ERROR } from '~/schemas/errorSchemas'
import { AnonSessionTokenSchema } from '~/schemas/sessionSchemas'
import { ANON_SESSION_ERROR_CODE, handleApiError } from './apiResponseHandler'

// =========================================================================
// SECTION 1: CONFIG / AXIOS CLIENT
// =========================================================================
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5004'
const SESSION_HEADER = 'GlofiSession'
/**
 * Sessão enviada nas chamadas feitas no servidor (home e páginas do CMS): a API exige o header em toda rota e só
 * confere se ele não está vazio, e o servidor não tem o cookie do visitante. Antes saía o texto "undefined".
 */
const SERVER_SESSION = 'server'
const NEW_SESSION_URL = /^\/?new-session(\?|$)/
/** Validade padrão dos cookies de sessão (token do cliente e sessão anônima), em minutos: 8 horas. */
const DEFAULT_TOKEN_MINUTES = 480

/**
 * Validade do cookie do token em segundos (o `maxAge` do nookies é em segundos), a partir de
 * `NEXT_PUBLIC_TOKEN_EXP_MINUTES`, que deve acompanhar o `JWT_EXPIRES_TIME` da API. Sem a variável, 8 horas. Vale para
 * a sessão anônima (aqui) e para o token do cliente (`SessionService`).
 */
export function getTokenMaxAgeSeconds(): number {
    const minutes = Number(process.env.NEXT_PUBLIC_TOKEN_EXP_MINUTES)
    return (Number.isFinite(minutes) && minutes > 0 ? minutes : DEFAULT_TOKEN_MINUTES) * 60
}

/**
 * Tempo limite padrão das chamadas: sem ele, uma API pendurada deixava o overlay de carregamento travando a loja. A
 * exceção é o `checkout/finish` (`timeout: 0` na chamada): cortar a espera não cancela a cobrança no servidor.
 */
export const API_TIMEOUT_MS = 30_000

/**
 * Tempo limite das leituras feitas no servidor para montar a página (home): com o padrão, uma chamada pendurada segurava
 * a resposta da página por 30 s. A parte que estoura some da tela, como quando falha.
 */
export const SERVER_READ_TIMEOUT_MS = 10_000

/** Tempo limite do `new-session`: a chamada que espera a sessão não fica presa se a API não responder. */
export const NEW_SESSION_TIMEOUT_MS = 5000

/**
 * Rotas que o `ValidateSessionTokenMiddleware` da API dispensa de sessão: as do início do caminho (`StartsWithSegments`)
 * e as que aparecem em qualquer parte dele (`Contains`). Elas não esperam nem abrem a sessão anônima.
 */
const PUBLIC_ROUTE_PREFIXES = ['/new-session', '/swagger/', '/hcheck', '/health']
const PUBLIC_ROUTE_PARTS = [
    '/Notification/Erede',
    '/product/xml',
    '/auth/set-glofi-token',
    '/auth/login-as-customer',
    '/pages/get-current-date-time',
]

export const SESSION_EXPIRED_MESSAGE = API_ERROR_MESSAGES.sessionExpired

// Endpoints de login (sem barra inicial e sem query): um 401 deles é credencial ou código errado, não sessão expirada.
const sessionExpiredUrlsWhiteList = ['auth/login', 'auth/fast-login', 'auth/custom-login', 'auth/login-as-customer']

const endpointPath = (url: string | undefined) => (url ?? '').split('?')[0].replace(/^\/+/, '')

const isBrowser = () => typeof window !== 'undefined'

function isPublicRoute(url: string | undefined): boolean {
    const path = `/${(url ?? '').split('?')[0].replace(/^\/+/, '')}`
    return (
        PUBLIC_ROUTE_PREFIXES.some((prefix) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix)) ||
        PUBLIC_ROUTE_PARTS.some((part) => path.includes(part))
    )
}

// =========================================================================
// SESSÃO ANÔNIMA (cookie `@glofi:AnonToken`, header `GlofiSession`)
// =========================================================================
/**
 * Cópia em memória da sessão anônima: no navegador que bloqueia cookies o cookie não grava, e sem a cópia cada chamada
 * abriria uma sessão nova (o carrinho anônimo se perderia a cada clique). Vale até a página recarregar.
 */
let memoryAnonSession: string | undefined
/** Abertura em andamento: as chamadas que chegam ao mesmo tempo esperam a mesma. Descartada ao terminar, com ou sem falha. */
let pendingAnonSession: Promise<string> | null = null

function knownAnonSession(): string | undefined {
    return parseCookies()[ANON_TOKEN_COOKIE] || memoryAnonSession
}

function openAnonSession(client: AxiosInstance): Promise<string> {
    if (!pendingAnonSession) {
        pendingAnonSession = client
            .get<unknown>('new-session', { timeout: NEW_SESSION_TIMEOUT_MS })
            .then(({ data }) => {
                const token = AnonSessionTokenSchema.parse(data)
                memoryAnonSession = token
                setCookie(null, ANON_TOKEN_COOKIE, token, {
                    maxAge: getTokenMaxAgeSeconds(),
                    httpOnly: false,
                    path: '/',
                })
                return token
            })
            .finally(() => {
                pendingAnonSession = null
            })
    }
    return pendingAnonSession
}

/**
 * Sessão anônima para as chamadas: a que está sendo aberta (inclusive por uma troca), a do cookie, a da memória ou,
 * sem nenhuma, uma nova pelo `new-session` (uma vez só para as chamadas simultâneas). Rejeita se não conseguir abrir;
 * se uma troca falhar, devolve a sessão anterior.
 */
export function ensureAnonSession(client: AxiosInstance = api): Promise<string> {
    const known = knownAnonSession()
    if (pendingAnonSession) {
        return pendingAnonSession.catch((error) => {
            if (known) return known
            throw error
        })
    }
    return known ? Promise.resolve(known) : openAnonSession(client)
}

/**
 * Troca a sessão anônima por uma nova (login de suporte). Reaproveita a abertura em andamento, para o visitante novo não
 * sair com duas sessões; a sessão anterior vale até a nova chegar.
 */
export function rotateAnonSession(client: AxiosInstance = api): Promise<string> {
    return openAnonSession(client)
}

/** Esquece a sessão da memória e a abertura em andamento. Só para os testes. */
export function resetAnonSessionState(): void {
    memoryAnonSession = undefined
    pendingAnonSession = null
}

async function attachSession(client: AxiosInstance, config: InternalAxiosRequestConfig) {
    if (NEW_SESSION_URL.test(config.url ?? '')) return config

    if (!isBrowser()) {
        config.headers[SESSION_HEADER] = SERVER_SESSION
    } else if (isPublicRoute(config.url)) {
        const known = knownAnonSession()
        if (known) config.headers[SESSION_HEADER] = known
    } else {
        try {
            config.headers[SESSION_HEADER] = await ensureAnonSession(client)
        } catch (cause) {
            console.error('Erro ao abrir a sessão anônima:', cause)
            // Sem sessão a API responde 405: a chamada nem sai, e o handleApiError mostra a mensagem da sessão.
            throw new AxiosError(API_ERROR_MESSAGES.sessionUnavailable, ANON_SESSION_ERROR_CODE, config)
        }
    }

    // Chamada com Authorization próprio (ex.: token do link de recuperação de senha) não leva o do cookie.
    const token = isBrowser() ? parseCookies()[TOKEN_COOKIE] : undefined
    if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`
        config.usesSessionToken = true
    }

    config.headers['Cache-Control'] = 'no-store, no-cache, must-revalidate, proxy-revalidate, max-age=0'
    return config
}

/**
 * 401 de uma chamada feita com o token da sessão (fora dos logins) encerra a sessão: apaga o cookie, limpa as stores e
 * avisa a aplicação pelo evento `sessionExpired` (o SessionWatcher da casca mostra o aviso e manda para o login). O 401 de
 * uma chamada com Authorization próprio é do token dela (link de recuperação), não da sessão.
 */
export function handleSessionExpiration(error: AxiosError): void {
    const config = error.config
    if (error.response?.status !== 401 || !config?.usesSessionToken) return
    if (sessionExpiredUrlsWhiteList.includes(endpointPath(config.url))) return
    if (!isBrowser()) return

    destroyCookie(null, TOKEN_COOKIE, { path: '/' })
    clearAllStores()
    window.dispatchEvent(
        new CustomEvent('sessionExpired', { detail: { type: 'sessionExpired', message: SESSION_EXPIRED_MESSAGE } }),
    )
}

/** Overlay de carregamento opt-in: `{ meta: { showLoader: true } }` na chamada. */
function attachLoaderInterceptors(client: AxiosInstance) {
    client.interceptors.request.use((config) => {
        if (config.meta?.showLoader) useLoaderStore.getState().startLoading()
        return config
    })
    client.interceptors.response.use(
        (response) => {
            if (response.config.meta?.showLoader) useLoaderStore.getState().stopLoading()
            return response
        },
        (error) => {
            if (error?.config?.meta?.showLoader) useLoaderStore.getState().stopLoading()
            return Promise.reject(error)
        },
    )
}

export function createApiClient(baseURL: string = API_URL): AxiosInstance {
    const instance = axios.create({ baseURL, timeout: API_TIMEOUT_MS })
    instance.interceptors.request.use((config) => attachSession(instance, config))
    instance.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
            handleSessionExpiration(error)
            return Promise.reject(error)
        },
    )
    attachLoaderInterceptors(instance)
    return instance
}

// =========================================================================
// SECTION 2: ERROR HANDLING
// =========================================================================
/**
 * Toast do erro no navegador. No servidor (server components, generateMetadata) não faz nada: o react-toastify é uma
 * referência de cliente e chamá-lo lançaria, escondendo o ApiError; quem chama no servidor decide pelo erro.
 */
export function defaultApiErrorHandler(error: ApiError) {
    if (!isBrowser()) return
    if (error.errors.length > 0) {
        toast.error(error.errors.map((e) => e.message).join('\n'), { autoClose: 3000 })
        return
    }
    toast.error(error.message || 'Ocorreu um erro inesperado. Tente novamente.', { autoClose: 5000 })
}

/**
 * Resposta 2xx que não passou no schema: erro de contrato, separado do erro HTTP. Registra o detalhe (método, rota e o
 * caminho dos campos, sem os valores) no console e no Sentry; o cliente vê só a mensagem genérica. O corpo cru vai em
 * `data`, para quem chama decidir se a operação valeu.
 */
function contractError(method: string, url: string, status: number, data: unknown, cause: z.ZodError): ApiError {
    reportContractIssue(
        `Resposta fora do contrato em ${method.toUpperCase()} ${url} (${status})`,
        describeIssues(cause.issues),
    )
    return { statusCode: status, statusError: CONTRACT_ERROR, message: API_ERROR_MESSAGES.contract, errors: [], data }
}

// =========================================================================
// SECTION 3: HIGH LEVEL SERVICE (execute() => Promise<T>)
// =========================================================================
type SuccessHandler<T> = (data: T) => void | Promise<void>
type ErrorHandler = (error: ApiError) => void | Promise<void>
/** `true` engole qualquer erro (resolve com `null`); uma função decide por erro. */
type SwallowOption = boolean | ((error: ApiError) => boolean)

interface IApiService<T> {
    onSuccess(handler: SuccessHandler<T>): IApiService<T>
    onError(
        handler: ErrorHandler,
        options?: { useDefaultErrorHandler?: boolean; swallow?: SwallowOption },
    ): IApiService<T>
    execute(): Promise<T>
}

function createHighLevelService<T>(
    apiInstance: AxiosInstance,
    method: 'get' | 'post' | 'put' | 'delete',
    url: string,
    schema: z.ZodType<T>,
    payload?: unknown,
    configOverride?: AxiosRequestConfig,
): IApiService<T> {
    let successHandler: SuccessHandler<T> = () => {}
    let errorHandler: ErrorHandler = defaultApiErrorHandler
    let hasCustomErrorHandler = false
    let swallowErrors: SwallowOption = false

    return {
        onSuccess(handler) {
            successHandler = handler
            return this
        },

        onError(handler, options = { useDefaultErrorHandler: true, swallow: false }) {
            hasCustomErrorHandler = true
            swallowErrors = options.swallow ?? false
            errorHandler =
                options.useDefaultErrorHandler === false
                    ? handler
                    : async (error) => {
                          defaultApiErrorHandler(error)
                          await handler(error)
                      }
            return this
        },

        async execute(): Promise<T> {
            try {
                // Corpo como função: a validação pelo schema de requisição roda aqui dentro, e um dado inválido vira
                // ApiError 422 pelo mesmo caminho dos outros erros (onError, toast padrão), sem sair requisição.
                const data = typeof payload === 'function' ? (payload as () => unknown)() : payload
                const response = await apiInstance.request({ method, url, data, ...(configOverride ?? {}) })
                const parsed = schema.safeParse(response.data)
                if (!parsed.success) {
                    throw contractError(method, url, response.status ?? 200, response.data, parsed.error)
                }
                const validatedData = parsed.data
                await successHandler(validatedData)
                return validatedData
            } catch (error) {
                const apiError = handleApiError(error)
                await errorHandler(apiError)

                // Por padrão PROPAGA o erro (o React Query precisa do reject para cair no onError).
                const shouldSwallow = typeof swallowErrors === 'function' ? swallowErrors(apiError) : swallowErrors
                if (!hasCustomErrorHandler || !shouldSwallow) throw apiError
                return null as unknown as T
            }
        },
    }
}

// =========================================================================
// SECTION 4: EXPORTS
// =========================================================================
export const api = createApiClient()

export function apiService(apiInstance: AxiosInstance) {
    return {
        get: <T>(url: string, schema: z.ZodType<T>, config?: AxiosRequestConfig) =>
            createHighLevelService(apiInstance, 'get', url, schema, undefined, config),

        post: <T>(url: string, payload: unknown, schema: z.ZodType<T>, config?: AxiosRequestConfig) =>
            createHighLevelService(apiInstance, 'post', url, schema, payload, config),

        put: <T>(url: string, payload: unknown, schema: z.ZodType<T>, config?: AxiosRequestConfig) =>
            createHighLevelService(apiInstance, 'put', url, schema, payload, config),

        delete: <T>(url: string, payload: unknown, schema: z.ZodType<T>, config?: AxiosRequestConfig) =>
            createHighLevelService(apiInstance, 'delete', url, schema, payload, config),
    }
}
